/**
 * Displays an info icon with a tooltip listing the other users of the recipient company
 * and their individual read status (read at timestamp or "unread").
 *
 * The backend already excludes the currently logged-in user from the {@code readBy} list,
 * so this component renders the list as-is without any additional filtering.
 * The backend is the single source of truth for this exclusion.
 *
 * Used exclusively on incoming USER_MESSAGEs in the chat window.
 */

import React, {useMemo} from "react";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {Table, TableBody, TableCell, TableRow, Tooltip} from "@mui/material";
import {useTranslation} from "react-i18next";
import {NotificationI18N} from "@/translations";
import {formatIsoDateTime} from "@/lib/utils";
import type {MessageReadByUserDTO} from "./types";

/**
 * Props for the ReadByTooltip component.
 *
 * @property {MessageReadByUserDTO[]} readBy - Read state entries for the other active users of
 *   the recipient's company. The backend omits the currently logged-in user from this list.
 *   An empty array causes the component to render nothing (no info icon is shown).
 *   Each entry's {@code userId} is guaranteed non-null by the backend contract
 *   ({@code MessageReadByUserDTO.userId} is a required {@code Long}) and is used as the React key.
 */
interface ReadByTooltipProps {
    readBy: MessageReadByUserDTO[];
}

/**
 * ReadByTooltip renders an info icon with a tooltip table showing each user's read status.
 * Returns null when the readBy list is empty (no icon is rendered). This also covers the
 * case where the logged-in user is the only active member of their company, since the
 * backend excludes them and the resulting list is empty.
 */
const ReadByTooltip: React.FC<ReadByTooltipProps> = ({readBy}) => {
    const {t, i18n} = useTranslation();

    const tooltipContent = useMemo(
            () => (
                    <Table
                            size="small"
                            sx={{
                                "& td, & th": {
                                    padding: "4px 8px",
                                    fontSize: "0.75rem",
                                    borderColor: "divider"
                                }
                            }}
                    >
                        <TableBody>
                            {readBy.map((entry) => (
                                    <TableRow key={entry.userId}>
                                        <TableCell component="th" sx={{fontWeight: "bold"}}>
                                            {entry.name}
                                        </TableCell>
                                        <TableCell>
                                            {entry.readAt
                                                    ? t(NotificationI18N.chat.readByTooltip.readAt, {
                                                        readAt: formatIsoDateTime(entry.readAt, i18n.language)
                                                    })
                                                    : t(NotificationI18N.chat.readByTooltip.unread)}
                                        </TableCell>
                                    </TableRow>
                            ))}
                        </TableBody>
                    </Table>
            ),
            [readBy, t, i18n.language]
    );

    if (readBy.length === 0) {
        return null;
    }

    return (
            <Tooltip
                    arrow
                    title={tooltipContent}
                    slotProps={{
                        tooltip: {
                            sx: {
                                maxWidth: "fit-content",
                                padding: "4px",
                                backgroundColor: "background.paper",
                                color: "text.primary"
                            }
                        },
                        arrow: {
                            sx: {
                                color: "background.paper"
                            }
                        }
                    }}
            >
                <InfoOutlinedIcon fontSize="small" sx={{cursor: "pointer", color: "action.active"}}/>
            </Tooltip>
    );
};

export default ReadByTooltip;
